import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../lib/api';

export type InvoiceDocumentUploadState = {
  orderDocumentId: string;
  status: string;
  quickBooksAttachableId: string | null;
};

export const invoiceAttachmentsKey = (orderId: string) =>
  ['org:invoice-attachments', orderId] as const;

export function useOrderInvoiceAttachments(orderId: string, enabled: boolean) {
  return useQuery({
    queryKey: invoiceAttachmentsKey(orderId),
    queryFn: async () => {
      const { data } = await api.get<{ attachments: InvoiceDocumentUploadState[] }>(
        `/organization/orders/${encodeURIComponent(orderId)}/invoice-attachments`
      );
      return data;
    },
    enabled: enabled && Boolean(orderId),
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export async function attachOrderInvoiceDocument(orderId: string, documentId: string) {
  const { data } = await api.post<{ attached: boolean; alreadyAttached?: boolean }>(
    `/organization/orders/${encodeURIComponent(orderId)}/invoice-attachments`,
    { documentId }
  );
  return data;
}

export function useAttachOrderInvoiceDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ orderId, documentId }: { orderId: string; documentId: string }) =>
      attachOrderInvoiceDocument(orderId, documentId),
    onSettled: (_result, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: invoiceAttachmentsKey(variables.orderId) });
    },
  });
}
